// ABOUTME: Hook for managing saved payment methods (cards).
// ABOUTME: Provides CRUD operations for payment methods with optimistic cache updates.

import { useCallback } from "react"
import { useQuery, useMutation } from "@apollo/client/react"
import {
  SavedPaymentMethodsDocument,
  DeletePaymentMethodDocument,
  SetDefaultPaymentMethodDocument,
} from "@/graphql/main/gql/graphql"
import { useNotificationContext } from "@/app/providers/useNotificationContext"
import { useTranslation } from "react-i18next"
import { addLogError } from "@/model/logger"

export const useSavedPaymentMethods = () => {
  const { t } = useTranslation()
  const { handleNotification } = useNotificationContext()

  // cache-and-network so the card list renders instantly from cache on
  // revisit while the fresh list is fetched in the background.
  const { data, loading, refetch } = useQuery(SavedPaymentMethodsDocument, {
    fetchPolicy: "cache-and-network",
  })

  const [deletePaymentMethodMutation, { loading: isDeleting }] = useMutation(DeletePaymentMethodDocument)
  const [setDefaultMutation, { loading: isSettingDefault }] = useMutation(SetDefaultPaymentMethodDocument)

  const paymentMethods = data?.savedPaymentMethods ?? []

  const deletePaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      try {
        await deletePaymentMethodMutation({
          variables: { paymentMethodId },
        })
        await refetch()
        handleNotification("success", t("common.paymentMethodDeleted"), true, 3000)
      } catch (error) {
        addLogError("Failed to delete payment method:", error)
        handleNotification("error", t("common.paymentMethodDeleteError"), true, 3000)
      }
    },
    [deletePaymentMethodMutation, refetch, handleNotification, t],
  )

  const setDefaultPaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      try {
        await setDefaultMutation({
          variables: { paymentMethodId },
        })
        await refetch()
        handleNotification("success", t("common.paymentMethodSetDefault"), true, 3000)
      } catch (error) {
        addLogError("Failed to set default payment method:", error)
        handleNotification("error", t("common.paymentMethodSetDefaultError"), true, 3000)
      }
    },
    [setDefaultMutation, refetch, handleNotification, t],
  )

  return {
    paymentMethods,
    loading,
    isDeleting,
    isSettingDefault,
    deletePaymentMethod,
    setDefaultPaymentMethod,
    refetch,
  }
}
